import { useState, useCallback, useRef, useEffect } from 'react';

export interface Toast {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message?: string;
  action?: { label: string; onClick: () => void };
  duration?: number; // ms, default 5000; 0 = persistent
}

export interface UseToastReturn {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION = 5000;

let toastIdCounter = 0;

function generateToastId(): string {
  toastIdCounter += 1;
  return `toast-${toastIdCounter}-${Date.now()}`;
}

/**
 * Toast notification state management hook.
 *
 * Each screen that uses it gets its own toast state/stack.
 * No shared context provider needed.
 *
 * - Auto-dismiss after `duration` ms (default 5000; 0 = persistent)
 * - Same-title toasts collapse: the newest replaces the older one in place
 * - Max 3 visible toasts; oldest dismissed when exceeded
 * - Each toast has optional action button (e.g., "Retry", "Switch to Audio")
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = generateToastId();
    const newToast: Toast = { ...toast, id };
    const duration = toast.duration ?? DEFAULT_DURATION;

    const dropTimer = (toastId: string) => {
      const timer = timersRef.current.get(toastId);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(toastId);
      }
    };

    setToasts(prev => {
      // Collapse repeats of the same notice. A cascade of failures fires one
      // degradation card per failing track, and stacked "Switched to Chat" cards each
      // hold their own timer — on the 2026-07-29 demo three of them sat on screen at
      // once. The newest copy replaces the older one and restarts the countdown, so a
      // repeat still reads as a fresh event without piling up.
      // Severity is part of the key: an info card must not be able to quietly evict
      // an error card that happens to share a title.
      const deduped = prev.filter(t => {
        if (t.title !== newToast.title || t.type !== newToast.type) return true;
        dropTimer(t.id);
        return false;
      });

      const updated = [...deduped, newToast];
      // Enforce max visible toasts: remove oldest when exceeded
      if (updated.length > MAX_VISIBLE_TOASTS) {
        const removed = updated.slice(0, updated.length - MAX_VISIBLE_TOASTS);
        // Clear timers for removed toasts
        removed.forEach(t => dropTimer(t.id));
        return updated.slice(updated.length - MAX_VISIBLE_TOASTS);
      }
      return updated;
    });

    // Auto-dismiss after duration (0 = persistent)
    if (duration > 0) {
      const timer = setTimeout(() => {
        removeToast(id);
      }, duration);
      timersRef.current.set(id, timer);
    }

    return id;
  }, [removeToast]);

  const clearAll = useCallback(() => {
    setToasts([]);
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  return {
    toasts,
    addToast,
    removeToast,
    clearAll,
  };
}
